// WCO-2594 — derive a raw-material lot's REMAINING cases from its live weight.
//
// Why: `ics.hte_lot.actual_quantity` is the RECEIVED case count. It is written once at receipt and
// never decremented on consumption (consumers only decrement `current_amount`, the live weight). So
// reading `actual_quantity` for "cases remaining" shows the received count no matter how much is used
// (e.g. prod lot `P LNBC ind`: 29 received cases still shown at 72.28 lb of 1812.4 lb → really ~1 case).
//
// How: use the lot's OWN received cases:weight ratio (`actual_quantity / actual_weight`), NOT the
// catalog `unit_weight`. This is catch-weight-safe (WCO-2586: cases↔weight is not a fixed catalog
// ratio) because each lot carries its own realized ratio.
//
// Rounding: FLOOR — matches the app-wide convention for "whole cases/boxes a weight represents"
// on-hand (e.g. BoxMaterialSection `hteCasesForDisplay = Math.floor(weight / unit_weight)`). CEIL in
// this codebase is reserved for "round up to COVER a shortfall" (transfer sizing), which this is not.
// The weight is always shown beside the case count, so a sub-case sliver stays visible.
//
// Read-side ONLY: this does not touch consumption/reserve/return/ledger/status logic — that weight &
// status accuracy is owned by WCO-2595 / WCO-2596.
//
// WCO-2746 — the same "ask the lot, not the catalog" rule now backs the create-session estimate, and
// that logic is shared with the backend via `@tnb/lot-cases` (re-exported below). Note the two
// different questions: `deriveRemainingCases` answers "how many cases are ON HAND" (FLOOR — a
// part-case is not a case), while `casesToOpen` answers "how many cases must be OPENED to draw this
// weight" (CEIL — you cannot draw 100 lbs from two 44-lb cases). Don't substitute one for the other.
//
// NOTE: `@tnb/lot-cases` is a CommonJS package linked via `file:`, so it is registered in
// vite.config.ts (`build.commonjsOptions.include` + `optimizeDeps.include`) for the CJS→ESM
// transform — same as @tnb/output-cases. Import into local bindings, then re-export (a bare
// `export ... from` of a CJS module is the fragile form; WCO-2546).

import {
  lotCaseWeight,
  lotWholeCases,
  lotLooseWeight,
  casesToOpen,
  aggregateLotAvailability,
  splitHteDraw,
} from '@tnb/lot-cases';

export { lotCaseWeight, lotWholeCases, lotLooseWeight, casesToOpen, aggregateLotAvailability, splitHteDraw };
export type { LotAvailability, HteDraw } from '@tnb/lot-cases';

export interface LotCaseInput {
  actual_quantity?: number | null;      // received case count (never decremented)
  actual_weight?: number | null;        // received weight, lb (the basis for the ratio)
  current_amount?: number | null;       // live remaining weight, lb
  // 'Y' = counted unit (boxes/bags), NOT weight-consumed.
  // ⚠️ WCO-2155: packing materials are no longer lot-tracked at all — receiving credits
  // ics.hte_consumable_stock (in pieces) instead of creating an ics.hte_lot row, and live
  // on-hand is served by the settings/mapping endpoints. This branch now only renders
  // LEGACY lot rows created before that change; their `actual_quantity` was never
  // decremented by consumption, so it shows the originally-received count, not live stock.
  is_packing_material?: string | null;
}

/**
 * Remaining cases of a RAW-MATERIAL lot, derived from live weight.
 * @returns whole cases remaining, or `null` when there is no basis to derive
 *          (no received cases, or no received weight — e.g. a weight-only/bulk lot).
 */
export function deriveRemainingCases(lot: LotCaseInput): number | null {
  const receivedCases = Number(lot.actual_quantity);
  const receivedWeight = Number(lot.actual_weight);
  const liveWeight = Number(lot.current_amount);

  // No case basis (not tracked in cases) or no weight basis → cannot derive.
  if (!(receivedCases > 0) || !(receivedWeight > 0)) return null;
  // Consumed / no weight left → zero cases remaining.
  if (!(liveWeight > 0)) return 0;

  return Math.floor(receivedCases * (liveWeight / receivedWeight));
}

/**
 * Cases to DISPLAY for a lot's current stock — the packing-aware entry point that
 * inventory surfaces should use (Material Lots Breakdown, Inventory dashboard).
 *
 * Packing materials are consumed by COUNT, not weight, so the weight-ratio derivation does not
 * apply — show their tracked count (`actual_quantity`) unchanged. Raw materials derive from weight.
 * @returns whole cases to show, or `null` to render '—'.
 */
export function displayRemainingCases(lot: LotCaseInput): number | null {
  if (lot.is_packing_material === 'Y') {
    const qty = Number(lot.actual_quantity);
    return qty > 0 ? Math.round(qty) : null;
  }
  return deriveRemainingCases(lot);
}
